import type {
  MiceDirectionAnalysis,
  MiceDirectionDecision,
  MiceRecommendation,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { flightLabel } from '../travelers/version_diff';
import { formatMoney } from './need_format';

type Direction = 'OUTWARD' | 'RETURN';

const DIRECTION = { OUTWARD: 'mice.table.outbound', RETURN: 'mice.table.inbound' } as const;

/** Resumo de um sentido: o que a busca trouxe, o que as premissas cortaram e o que o agente comparou. */
function summary(a: MiceDirectionAnalysis): string[] {
  const found = t('mice.needs.analysis.found', { count: a.found });
  const discarded =
    a.discarded > 0
      ? t('mice.needs.analysis.discarded', {
          count: a.discarded,
          reasons: a.discardReasons.join('; '),
        })
      : null;
  // Parcela zerada sai: no pt-BR o 0 cai no singular ("0 tolerado").
  const tolerated = a.analyzed - a.ideal;
  const detail = [
    a.ideal > 0 ? t('mice.needs.analysis.ideal', { count: a.ideal }) : null,
    tolerated > 0 ? t('mice.needs.analysis.tolerated', { count: tolerated }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  const compared = t('mice.needs.analysis.compared', { count: a.analyzed, detail });
  const range = a.priceRange
    ? t('mice.needs.analysis.priceRange', {
        min: formatMoney(a.priceRange.min),
        max: formatMoney(a.priceRange.max),
      })
    : null;
  return [
    [t(DIRECTION[a.direction]), found, discarded].filter(Boolean).join(' · '),
    [compared, range].filter(Boolean).join(' · '),
  ];
}

/** Os voos que perderam para o escolhido, com o porquê que o código montou. */
const passedOverLines = (a: MiceDirectionAnalysis): string[] =>
  a.passedOver.map((f) =>
    t('mice.calc.passedOver', { flight: flightLabel(f), price: formatMoney(f.price), why: f.why }),
  );

/** A conta da troca por preço nas tarifas que o agente comparou, contra o limite do evento. A do
 *  ponto de partida (o pedido, quando foi encontrado) é a escolhida mais a economia: a tarifa
 *  cotada pode ser outra que o preço da busca. */
function swapLine(rec: MiceRecommendation, d: MiceDirectionDecision | undefined): string | null {
  const chosen = d?.direction === 'OUTWARD' ? rec.outbound : rec.inbound;
  if (d?.decision !== 'swapped_for_price' || !d.savings || !d.swapLimit || !chosen) return null;
  const start = {
    amount: Math.round((chosen.farePrice.amount + d.savings.amount) * 100) / 100,
    currency: chosen.farePrice.currency,
  };
  return t(d.requestedStatus === 'found' ? 'mice.calc.swap' : 'mice.calc.swapFromStart', {
    requested: formatMoney(start),
    chosen: formatMoney(chosen.farePrice),
    savings: formatMoney(d.savings),
    limit: formatMoney(d.swapLimit),
  });
}

function linesOf(rec: MiceRecommendation, direction: Direction): string[] {
  const analysis = rec.analysis.find((a) => a.direction === direction);
  const swap = swapLine(
    rec,
    rec.directions.find((d) => d.direction === direction),
  );
  return [
    ...(analysis ? [...summary(analysis), ...passedOverLines(analysis)] : []),
    ...(swap ? [swap] : []),
  ];
}

/** A memória de cálculo de uma versão, recolhida: por sentido, o que a busca trouxe, os preteridos
 *  e a conta da troca. */
export function CalcMemory({ rec }: Readonly<{ rec: MiceRecommendation }>) {
  const groups = (['OUTWARD', 'RETURN'] as const)
    .map((direction) => ({ direction, lines: linesOf(rec, direction) }))
    .filter((g) => g.lines.length > 0);
  if (groups.length === 0) return null;
  return (
    <details className="rounded-lg border border-border-soft px-3 py-2 text-meta text-muted-foreground">
      <summary className="cursor-pointer font-medium text-foreground">
        {t('mice.calc.title')}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        {groups.map(({ direction, lines }) => (
          <ul key={direction} className="flex flex-col gap-0.5">
            {lines.map((line, i) => (
              <li key={`${direction}:${String(i)}`}>{line}</li>
            ))}
          </ul>
        ))}
      </div>
    </details>
  );
}
